// Regras puras da tela de Aniversários (/birthday-settings) e dos seus modais.
// Separadas dos componentes para ficarem testáveis sem montar o React.
import { i18n } from "../../translate/i18n";
import { AEROS } from "../../styles/aerosTokens";

// Variáveis aceitas pelo backend em `contactBirthdayMessage` /
// `userBirthdayMessage` (BirthdayService faz o replace no envio). A mensagem
// da equipe não recebe {idade}: o informativo fala do colega, não da idade.
export const CONTACT_VARIABLES = ["{nome}", "{idade}"];
export const USER_VARIABLES = ["{nome}"];

/**
 * Substitui as variáveis pelo valor real, do mesmo jeito que o backend faz.
 *
 * Idade ausente vira string vazia — é exatamente o que o contato receberia,
 * e é isso que a pré-visualização precisa mostrar (o aviso de "vai sair
 * vazio" fica a cargo de `missingVariables`, que separa os dois papéis).
 */
export const resolveMessage = (template, { name, age } = {}) => {
  if (!template) return "";
  const idade = age === 0 || age ? String(age) : "";
  return template
    .replace(/{nome}/g, name || "")
    .replace(/{idade}/g, idade);
};

/**
 * Variáveis usadas no texto que NÃO têm valor para este destinatário.
 *
 * Serve para o aviso âmbar do modal de teste: um contato sem data de
 * nascimento faz `{idade}` sumir da mensagem sem nenhum erro visível.
 */
export const missingVariables = (template, { name, age } = {}) => {
  if (!template) return [];
  const faltando = [];
  if (/{nome}/.test(template) && !name) faltando.push("{nome}");
  if (/{idade}/.test(template) && !(age === 0 || age)) faltando.push("{idade}");
  return faltando;
};

/**
 * Insere a variável na posição do cursor, trocando a seleção quando há uma.
 *
 * Devolve também o novo cursor para o componente reposicioná-lo depois do
 * texto inserido — sem isso o cursor volta para o fim do campo e digitar
 * logo em seguida escreve no lugar errado.
 */
export const insertVariableAt = (text = "", variable, start, end) => {
  const inicio = Number.isInteger(start) ? start : text.length;
  const fim = Number.isInteger(end) ? end : inicio;
  const antes = text.slice(0, inicio);
  const depois = text.slice(fim);
  return {
    text: `${antes}${variable}${depois}`,
    cursor: inicio + variable.length,
  };
};

// "JS" a partir de "João Silva"; uma letra só quando o nome tem uma palavra.
export const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0])
    .join("")
    .toUpperCase();

// Cor determinística do avatar: o mesmo contato mantém a cor entre renders.
export const avatarColor = (seed = 0) =>
  AEROS.avatarCycle[Math.abs(Number(seed) || 0) % AEROS.avatarCycle.length];

/**
 * Dias até o próximo aniversário, ignorando o ano de nascimento.
 *
 * 29/02 em ano comum cai em 01/03 (o `Date` normaliza sozinho) — melhor
 * antecipar um dia que sumir com a pessoa do painel por três anos.
 */
export const daysUntilBirthday = (birthDate, hoje = new Date()) => {
  if (!birthDate) return null;
  const nascimento = new Date(birthDate);
  if (Number.isNaN(nascimento.getTime())) return null;

  const base = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const mes = nascimento.getUTCMonth();
  const dia = nascimento.getUTCDate();

  let proximo = new Date(base.getFullYear(), mes, dia);
  if (proximo < base) proximo = new Date(base.getFullYear() + 1, mes, dia);

  return Math.round((proximo - base) / (1000 * 60 * 60 * 24));
};

/**
 * Estado da linha em "Próximos aniversários".
 *
 * "Enviada" vence "Hoje": quando a mensagem já saiu, o que a pessoa precisa
 * saber é que está resolvido, não que a data é hoje.
 */
export const upcomingStatus = ({ days, messageSent }) => {
  if (messageSent) return "sent";
  if (days === 0) return "today";
  return "scheduled";
};

export const upcomingStatusLabel = (status) =>
  i18n.t(`aeros.birthday.status.${status}`);

export const upcomingStatusTone = (status, theme) => {
  if (status === "sent") return AEROS.success;
  if (status === "today") return AEROS.cyan;
  return theme?.palette?.text?.secondary || "#9CA3B8";
};

/**
 * "09:00" a partir de "09:00:00" — o backend guarda TIME com segundos, e o
 * mockup mostra só horas e minutos (o input type=time também espera HH:MM).
 */
export const trimSeconds = (time) => {
  if (!time) return "";
  const partes = String(time).split(":");
  return partes.length >= 2 ? `${partes[0]}:${partes[1]}` : String(time);
};

/**
 * Pill do cabeçalho: "Próximo envio hoje às 09:00 · 3 contatos".
 *
 * Sem contatos aniversariando hoje a frase muda para a data do próximo — dizer
 * "hoje às 09:00 · 0 contatos" anunciaria um envio que não vai acontecer.
 */
export const nextSendLabel = ({ time, todayCount, enabled }) => {
  if (!enabled) return i18n.t("aeros.birthday.nextSend.disabled");
  const hora = trimSeconds(time);
  if (!todayCount) return i18n.t("aeros.birthday.nextSend.idle", { time: hora });
  // Chave própria no singular: o i18n do projeto não tem regra de plural
  // configurada, então "1 contatos" apareceria tal e qual.
  const chave =
    todayCount === 1 ? "aeros.birthday.nextSend.todayOne" : "aeros.birthday.nextSend.today";
  return i18n.t(chave, { time: hora, count: todayCount });
};

// "07" / "SET" para a coluna de data do painel de próximos aniversários.
export const dayMonthParts = (birthDate) => {
  if (!birthDate) return { day: "", month: "" };
  const data = new Date(birthDate);
  if (Number.isNaN(data.getTime())) return { day: "", month: "" };
  const day = String(data.getUTCDate()).padStart(2, "0");
  const month = i18n.t(`aeros.birthday.months.${data.getUTCMonth()}`);
  return { day, month };
};

/**
 * Idade que a pessoa completa (ou completou) neste ano.
 *
 * O backend faz a mesma conta simples de diferença de anos ao montar os
 * aniversariantes do dia — a pré-visualização precisa bater com o que o
 * contato vai receber, então a regra aqui é a mesma, não a idade exata.
 */
export const ageFromBirthDate = (birthDate, hoje = new Date()) => {
  if (!birthDate) return null;
  const nascimento = new Date(birthDate);
  if (Number.isNaN(nascimento.getTime())) return null;
  return hoje.getFullYear() - nascimento.getUTCFullYear();
};

// "22/11/1988" para a linha do contato na busca do modal de teste.
export const formatBirthDate = (birthDate) => {
  if (!birthDate) return "";
  const data = new Date(birthDate);
  if (Number.isNaN(data.getTime())) return "";
  const dia = String(data.getUTCDate()).padStart(2, "0");
  const mes = String(data.getUTCMonth() + 1).padStart(2, "0");
  return `${dia}/${mes}/${data.getUTCFullYear()}`;
};

// "30 anos" / "1 ano" — sem regra de plural configurada no i18n do projeto.
export const ageLabel = (age) => {
  if (!(age === 0 || age)) return "";
  return i18n.t(age === 1 ? "aeros.birthday.ageOne" : "aeros.birthday.age", {
    count: age,
  });
};
